import { EMPTY_ENCODING, EncField, Encoding, VariableType } from '@mode-vegas/vegas';

import { Names } from '@mode-switch/model';
import { EncodingHelpers, SwitchSchemaModel } from '@mode/charts/util-vegas';
import { BLANK_HISTORY, ChartState } from '@mode/legacy/types-app-state';
import { ChartTypes, FlamingoResponse, FlamingoTypes, OrgTypes } from '@mode/shared/contract-common';
import { CompiledFormula, Switch } from '@mode/shared/util-switch';
import { rawNumber } from './axel/format/number.format';
import { ZoneType } from './chart-encoding-helpers';

const numberFormatter = rawNumber(null, { thousands: ',' });

/**
 * Returns true if the provided ChartType is not a table, pivotTable, or bigValue
 */
export function isChart(chartType: ChartTypes.ChartType) {
  return !['bigValue', 'pivotTable', 'table'].includes(chartType);
}

export function getExistingValueField(view: FlamingoTypes.ModeView, enc: Encoding, field: CompiledFormula): EncField {
  let formula: string;
  const expr = Switch.compileToExpr(Switch.Models.makeFromView(view.name, view), view.name, field.formula);
  if (expr != null) {
    const aggExpr = Switch.Expressions.isAggregate(expr);

    if (aggExpr != null && field.isField) {
      formula = EncodingHelpers.buildUpdateFieldParams(field, ZoneType.Aggregate).formula;
    } else {
      formula = EncodingHelpers.buildDefaultEncField({ field, zone: ZoneType.Aggregate }).formula;
    }
  }

  return enc.value.find((encField) => encField.formula === formula);
}

export function getExistingRowField(view: FlamingoTypes.ModeView, enc: Encoding, field: CompiledFormula): EncField {
  let formula: string;
  // TODO: Consolidate this block of code with the one in the function above
  const expr = Switch.compileToExpr(Switch.Models.makeFromView(view.name, view), view.name, field.formula);
  if (expr != null) {
    if (Switch.Expressions.isAggregate(expr) && field.isField) {
      formula = EncodingHelpers.buildUpdateFieldParams(field, ZoneType.Aggregate).formula;
    } else if (field.isField === false) {
      formula = EncodingHelpers.buildDefaultEncField({ field, zone: ZoneType.Aggregate }).formula;
    }
  }

  return (
    enc.y.find((encField) => encField.formula === formula) ||
    enc.y2.find((encField) => encField.formula === formula) ||
    enc.row.find((encField) => encField.formula === formula)
  );
}

export function getExistingColumnField(view: FlamingoTypes.ModeView, enc: Encoding, field: CompiledFormula): EncField {
  let formula: string;
  // TODO: Consolidate this block of code with the one in the function above
  const expr = Switch.compileToExpr(Switch.Models.makeFromView(view.name, view), view.name, field.formula);
  if (expr != null) {
    if (Switch.Expressions.isAggregate(expr) && field.isField) {
      formula = EncodingHelpers.buildUpdateFieldParams(field, ZoneType.Aggregate).formula;
    } else if (field.isField === false) {
      formula = EncodingHelpers.buildDefaultEncField({ field, zone: ZoneType.Aggregate }).formula;
    }
  }

  return (
    enc.x.find((encField) => encField.formula === formula) ||
    enc.x2.find((encField) => encField.formula === formula) ||
    enc.column.find((encField) => encField.formula === formula)
  );
}

export function checkCanSort(encoding: Encoding, chartType: ChartTypes.ChartType): boolean {
  switch (chartType) {
    case 'pie':
    case 'donut':
      return (
        encoding.marks.properties.angle.values.length > 0 &&
        encoding.marks.properties.color.values.length > 0 &&
        encoding.marks.properties.color.values[0].variable.type === VariableType.DISCRETE
      );
    case 'bar':
    case 'hBar':
      // Don't allow sorting of a bar chart with only one measure value and nothing in color
      return (
        encoding.value.length > 1 ||
        (encoding.marks.series.length > 0 &&
          encoding.marks.series[0].properties.color.values.length > 0 &&
          encoding.marks.series[0].properties.color.values[0].formula !== Names.Key)
      );
    case 'bigValue':
      return encoding.y.length > 0 && encoding.marks.properties.text.values.length > 0;
    case 'vegas': {
      return EncodingHelpers.findSortByFields(encoding, chartType) !== undefined;
    }
    default: {
      const { primary, secondary } = EncodingHelpers.extractPrimarySecondaryAxes(encoding);
      return primary.length > 0 && secondary.length > 0 && secondary[0].variable.type === VariableType.DISCRETE;
    }
  }
}

export function checkCanSwap(encoding: Encoding, chartType: ChartTypes.ChartType): boolean {
  switch (chartType) {
    case 'hBar':
    case 'bar':
    case 'stackedBar':
    case 'hStackedBar':
    case 'stackedBar100':
    case 'hStackedBar100':
    case 'vegas':
    case 'vegasPivotTable':
      // A bit simple, but shouldn't be too bad
      return true;
    default:
      return false;
  }
}

export function validateYAxis(axis: ChartTypes.ChartFormat['yAxis'] | ChartTypes.ChartFormat['y2Axis']): boolean {
  // Make sure if y-axis has log scale, range can't be 0 or lower
  if (axis != null && axis.scaleType === 'logarithmic' && axis.numberRange != null && axis.numberRange.min != null) {
    const min = typeof axis.numberRange.min === 'string' ? parseFloat(axis.numberRange.min) : axis.numberRange.min;
    if (min <= 0) {
      return false;
    }
  }

  return true;
}

export function isValidChartFormat(format: ChartTypes.ChartFormat): boolean {
  if (format) {
    if (!validateYAxis(format.yAxis)) {
      return false;
    }

    if (!validateYAxis(format.y2Axis)) {
      return false;
    }
  }

  return true;
}

// TODO: This should do something else if we've selected something that is not a chart
export function getEncodedChartInNormalizedChartArray(
  charts: ChartTypes.ChartFeatureState,
  token: string
): ChartTypes.EncodedChart {
  return {
    token: token,
    resource: charts.resources.get(token),
    queryToken: charts.queryTokens.get(token),
    switchViewToken: charts.switchViewTokens.get(token),
    version: charts.versions.get(token),

    // Available in case legacy chart components need to use the new state. Ideally they will be deprecated
    // and won't ever need to be migrated.
    v1View: charts.v1Views.get(token),

    history: charts.histories.has(token) ? charts.histories.get(token) : BLANK_HISTORY,

    encoding: charts.encodings.get(token),
    format: charts.formats.get(token),
    fieldFormats: charts.fieldFormats.get(token),
    fieldColors: charts.fieldColors.get(token),
    facetSizes: charts.facetSizes.get(token),
    chartType: charts.chartTypes.get(token),
    title: charts.titles.get(token),
    description: charts.descriptions.get(token),
    caption: charts.captions.get(token),
    colorPaletteToken: charts.colorPaletteTokens.get(token),
    isAutoUpdateDisabled: charts.isAutoUpdateDisabled.get(token) ?? false,
  };
}

export function getChartState(
  chart: ChartTypes.EncodedChart,
  model: FlamingoTypes.ModeModel,
  viewName: string
): ChartState {
  let encResult: EncodingHelpers.EncResult;

  if (chart.version === 2) {
    encResult = new EncodingHelpers.EncResult();
    if (!chart.encoding) {
      encResult = new EncodingHelpers.EncResult(EncodingHelpers.initEncoding(chart.chartType));
    } else {
      encResult = new EncodingHelpers.EncResult(EncodingHelpers.loadEncoding(chart.encoding));
    }
  } else {
    encResult = new EncodingHelpers.EncResult(EncodingHelpers.loadEncoding(EMPTY_ENCODING)); // Dummy encoding to avoid potential null reference errors
  }

  let validationResults = {
    canSort: false,
    canSwap: false,
    canRender: false,
    isCompleteEncoding: false,
    isValidEncoding: true,
    invalidEncodingErrors: [],
    canGoUp: false,
    canGoDown: false,
  };

  if (model) {
    const errors = EncodingHelpers.validateChartEncodingFields(
      encResult.encoding,
      SwitchSchemaModel.make(model, viewName)
    );
    const isComplete = EncodingHelpers.isChartEncodingComplete(encResult.encoding, chart.chartType);
    const canHierarchy = EncodingHelpers.canChangeHierarchy(encResult.encoding, model, viewName);

    validationResults = {
      canSort: checkCanSort(encResult.encoding, chart.chartType) && errors.length <= 0,
      canSwap: checkCanSwap(encResult.encoding, chart.chartType) && errors.length <= 0,
      canRender: errors.length <= 0 && isComplete,
      isCompleteEncoding: isComplete,
      isValidEncoding: errors.length <= 0,
      invalidEncodingErrors: errors,
      canGoUp: canHierarchy.back,
      canGoDown: canHierarchy.forward,
    };
  }

  return {
    token: chart.token,
    resource: chart.resource,
    v1View: chart.v1View,
    queryToken: chart.queryToken,
    switchViewToken: chart.switchViewToken,
    version: chart.version,

    history: chart.history,

    chartType: chart.chartType,
    colorPaletteToken: chart.colorPaletteToken,
    encResult: encResult,
    format: chart.format,
    fieldFormats: chart.fieldFormats,
    fieldColors: chart.fieldColors,
    facetSizes: chart.facetSizes,
    title: chart.title,
    description: chart.description,
    caption: chart.caption,
    isAutoUpdateDisabled: chart.isAutoUpdateDisabled ?? false,

    canRedo: chart.history.redoStack.length > 0,
    canUndo: chart.history.undoStack.length > 0,
    undoDesc: chart.history.redoStack.length ? chart.history.redoStack[0].action : '',
    redoDesc: chart.history.undoStack.length ? chart.history.undoStack[0].action : '',
    ...validationResults,
  };
}

export function getFriendlyFlamingoError(
  response: FlamingoResponse.FlamingoErrorResponse
): FlamingoResponse.FriendlyFlamingoError {
  const defaultError = {
    msg: 'Unknown error executing chart query',
    helpText: 'Please refresh the page to try again',
  };
  if (response) {
    switch (response.type) {
      case FlamingoResponse.ResponseType.DatasetNotFound:
        return {
          msg: 'Unable to find the dataset',
          helpText: 'Please rerun the SQL query for this chart',
        };
      case FlamingoResponse.ResponseType.SignatureExpired:
        return {
          msg: 'Authentication failed',
          helpText: 'Please refresh the page and try again',
        };
      case FlamingoResponse.ResponseType.SizeLimit:
        return {
          msg: 'Dataset size limit reached',
          helpText: `Please reduce the result size to ${numberFormatter(response.limit)} bytes or less`,
        };
      case FlamingoResponse.ResponseType.DatasetFailed:
        if (
          response.result &&
          (response.result?.stateProperties as FlamingoResponse.HelixEmptyDatasetProperties)?.code === 'EMPTY_DATASET'
        ) {
          return {
            msg: 'No results',
          };
        }
        return defaultError;
    }
  }

  return defaultError;
}

export function isColorPaletteCompatible(vizType: ChartTypes.ChartType, palette: OrgTypes.ColorPalette) {
  switch (vizType) {
    case 'bigValue':
      return palette.type === 'divergent';
    default:
      return palette.type === 'categorical';
  }
}

export function getDefaultColorPaletteForType(
  vizType: ChartTypes.ChartType,
  palettes: ReadonlyArray<OrgTypes.ColorPalette>
) {
  switch (vizType) {
    case 'bigValue':
      const divergent = palettes.filter((p) => p.type === 'divergent');
      return divergent.find((p) => p.isDefault) || divergent[0];
    case 'vegasPivotTable':
      const sequential = palettes.filter((p) => p.type === 'sequential');
      return sequential.find((p) => p.isDefault) || sequential[0];
    default:
      const categorical = palettes.filter((p) => p.type === 'categorical');
      return categorical.find((p) => p.isDefault) || categorical[0];
  }
}
